import React, { useEffect, useState } from 'react';
import { Paperclip, Download, ImageOff } from 'lucide-react';
import { getChannelCryptoKey, decryptBuffer } from '../utils/chatCrypto';
import { isTauri } from '../utils/tauri';
import { LightboxImage } from './ImageLightbox';
import './Chat.css';

/**
 * Rendering for client-side-encrypted chat attachments (metadata.enc set).
 *
 * Ciphertext is fetched with the webview's own fetch(), NOT the Tauri Rust
 * HTTP adapter — the Rust command returns response bodies as lossy UTF-8
 * strings, which corrupts binary data. The bucket allows GET from any origin
 * (objects are public ciphertext), so CORS is not an issue.
 */

// Small LRU of decrypted object URLs so scrollback doesn't refetch/redecrypt.
const CACHE_MAX = 50;
const cache = new Map(); // ciphertext url -> object URL

function cacheGet(url) {
  if (!cache.has(url)) return null;
  const v = cache.get(url);
  cache.delete(url);
  cache.set(url, v); // refresh recency
  return v;
}

function cachePut(url, objectUrl) {
  if (cache.has(url)) {
    URL.revokeObjectURL(cache.get(url));
    cache.delete(url);
  }
  cache.set(url, objectUrl);
  if (cache.size > CACHE_MAX) {
    const oldest = cache.keys().next().value;
    URL.revokeObjectURL(cache.get(oldest));
    cache.delete(oldest);
  }
}

async function fetchDecryptedBlob(channelId, url, mime) {
  const key = await getChannelCryptoKey(channelId);
  const res = await fetch(url);
  if (!res.ok) throw new Error(`fetch failed (${res.status})`);
  const buf = await res.arrayBuffer();
  return decryptBuffer(key, buf, mime);
}

async function fetchDecryptedObjectUrl(channelId, url, mime) {
  const cached = cacheGet(url);
  if (cached) return cached;
  const blob = await fetchDecryptedBlob(channelId, url, mime);
  const objectUrl = URL.createObjectURL(blob);
  cachePut(url, objectUrl);
  return objectUrl;
}

/**
 * Inline decrypted image (kind: "image"). `box` ({width, height}) is the
 * pre-computed display size from the message's stored dimensions — applied to
 * both the shimmer placeholder and the final image so the swap (and the
 * initial mount) never changes the row's height.
 */
export function EncryptedImage({ channelId, url, mime, name, box }) {
  const [objectUrl, setObjectUrl] = useState(() => cacheGet(url));
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    setError(false);
    setObjectUrl(cacheGet(url));
    if (!url || !channelId) return undefined;
    fetchDecryptedObjectUrl(channelId, url, mime)
      .then((u) => {
        if (alive) setObjectUrl(u);
      })
      .catch((e) => {
        console.warn('[chat] attachment decrypt failed:', e?.message || e);
        if (alive) setError(true);
      });
    return () => {
      alive = false;
    };
  }, [channelId, url, mime]);

  if (error) {
    return (
      <div className="chat-attach-img chat-attach-broken">
        <ImageOff size={20} strokeWidth={1.75} />
        Unable to decrypt image
      </div>
    );
  }
  if (!objectUrl) {
    return <div className="chat-attach-img chat-attach-loading" style={box || undefined} aria-label="Decrypting image…" />;
  }
  return <LightboxImage className="chat-attach-img" src={objectUrl} alt={name || 'image'} style={box || undefined} />;
}

/** Decrypt-and-save chip for non-image files (kind: "file"). */
export function EncryptedFileChip({ channelId, url, mime, name }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);

  const download = async () => {
    if (busy) return;
    setBusy(true);
    setError(false);
    try {
      const blob = await fetchDecryptedBlob(channelId, url, mime);
      const filename = name || 'attachment';

      if (isTauri()) {
        const [{ save }, { writeFile }] = await Promise.all([
          import('@tauri-apps/plugin-dialog'),
          import('@tauri-apps/plugin-fs'),
        ]);
        const path = await save({ defaultPath: filename });
        if (path) await writeFile(path, new Uint8Array(await blob.arrayBuffer()));
      } else {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = filename;
        a.click();
        setTimeout(() => URL.revokeObjectURL(a.href), 30_000);
      }
    } catch (e) {
      console.warn('[chat] attachment download failed:', e?.message || e);
      setError(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <button type="button" className="chat-file-chip" onClick={download} disabled={busy}>
      {busy ? <Download size={14} /> : <Paperclip size={14} />}
      {name || 'attachment'}
      {error && <span style={{ color: '#ef4444', marginLeft: 4 }}>failed</span>}
    </button>
  );
}
